import {Currency, CurrencyAmount} from 'util/currency';
import {DateTime, TimeSpan, TimeZone} from 'util/time';
import {SpacePricingCalendar, SpaceSamplePrice, SpaceWithSamplePrices} from 'services/entities/pricing';
import {getCacheItem, setCacheItem} from 'util/cache';
import {callAPI} from 'services/api';
import {TaxType} from 'services/entities/taxes';
import {readCurrency} from 'services/currency';
import {BookingSettings} from 'services/entities/booking';
import {applyTaxes} from 'util/pricing';
import {NotFoundError} from "util/errors";

const PRICING_CALENDARS_CACHE_KEY = 'pricing.pricing-calendars';
const TAXES_CACHE_KEY = 'pricing.taxes';
const MAX_CACHED_CALENDARS = 5;
const SETTINGS_CACHE_KEY = 'booking.settings';
const SAMPLE_PRICES_CACHE_KEY = 'pricing.spaces-sample-prices';

let _settingsPromise:Promise<BookingSettings>|null = null;

export async function loadSettings(): Promise<BookingSettings> {
  let settings: BookingSettings = getCacheItem(SETTINGS_CACHE_KEY);
  if(settings) {
    return settings;
  }
  if(_settingsPromise) {
    return _settingsPromise;
  }
  _settingsPromise = new Promise<BookingSettings>((resolve, reject) => {
    callAPI('/bookings/settings/', 'GET').then(
      response => {
        settings = {
          minTime: new TimeSpan({hours: response.min_hour}),
          maxTime: new TimeSpan({hours: response.max_hour}),
          minDuration: TimeSpan.parse(response.min_duration),
          priceDuration: TimeSpan.parse(response.price_duration),
          increment: TimeSpan.parse(response.increment),
          minDateOffset: response.min_date_offset,
          defaultTimeZone: response.default_time_zone,
          finalPaymentDateOffset: response.final_payment_date_offset,
          flexiTicketRate: response.flexi_ticket_rate,
          flexiTicketMinDateOffset: response.flexi_ticket_min_date_offset
        };
        setCacheItem(SETTINGS_CACHE_KEY, settings);
        resolve(settings);
      },
      ex => {
        _settingsPromise = null;
        reject(ex);
      }
    )
  });
  return _settingsPromise;
}

function readSpacePrice(value: any, currency: Currency) {
  return {
    price: CurrencyAmount.parse(value.price, currency),
    day: value.day,
    startTime: TimeSpan.parse(value.start_time),
    endTime: TimeSpan.parse(value.end_time)
  };
}

function readSpacePricingCalendar(value: any, defaultTimeZone: TimeZone): SpacePricingCalendar {
  const currency = readCurrency(value.currency);
  const prices = value.prices ? value.prices.map((p:any) => readSpacePrice(p, currency)) : [];
  return {
    space: value.space,
    currency: currency,
    startDate: DateTime.parse(value.start_date, defaultTimeZone),
    endDate: value.end_date ? DateTime.parse(value.end_date, defaultTimeZone) : null,
    prices: prices
  };
}

export function isDatePricingCalendar(date: DateTime, calendar: SpacePricingCalendar): boolean {
  return calendar.startDate.lessThanOrEqual(date!, 'day') && (!calendar.endDate || calendar.endDate.greaterThanOrEqual(date!, 'day'));
}

export async function loadSpacePricingCalendar(spaceCode: string, date?: DateTime): Promise<SpacePricingCalendar|null> {
  if(!date) {
    date = DateTime.today();
  }
  let cachedCalendars: SpacePricingCalendar[] = getCacheItem(PRICING_CALENDARS_CACHE_KEY);
  if(cachedCalendars) {
    const calendar = cachedCalendars.find(c => c.space === spaceCode && isDatePricingCalendar(date!, c));
    if(calendar) {
      // TODO: move calendar to end of cached array?
      return calendar;
    }
  }
  let response;
  try {
    response = await callAPI(`/spaces/${spaceCode}/pricing/?date=${date.format('YYYY-MM-DD')}`, 'GET', null, true);
  } catch(err) {
    if(err instanceof NotFoundError) {
      return null;
    } else {
      throw err;
    }
  }
  const settings = await loadSettings();
  const calendar = readSpacePricingCalendar(response, settings.defaultTimeZone);
  if(!cachedCalendars) {
    cachedCalendars = [];
  }
  cachedCalendars.push(calendar);
  if(cachedCalendars.length > MAX_CACHED_CALENDARS) {
    cachedCalendars = cachedCalendars.slice(cachedCalendars.length - MAX_CACHED_CALENDARS);
  }
  setCacheItem(PRICING_CALENDARS_CACHE_KEY, cachedCalendars, new TimeSpan({hours: 1}));
  return calendar;
}

export function getTimePrice(date: DateTime, calendar: SpacePricingCalendar): CurrencyAmount|null {
  const dayOfWeek = date.dayOfWeek;
  const time = date.time;
  const price =  calendar.prices.find(price => {
    return price.day === dayOfWeek && time.isBetween(price.startTime, price.endTime, true, false);
  });
  return price ? price.price : null;
}

export async function loadTimeRangePrice(date: DateTime, startTime: TimeSpan, endTime: TimeSpan, spaceCode: string,
                                         includeTaxes: boolean = false): Promise<CurrencyAmount|null> {
  const calendar = await loadSpacePricingCalendar(spaceCode, date);
  if(!calendar) {
    return null;
  }
  const settings = await loadSettings();
  const dayOfWeek = date.dayOfWeek;
  let time = startTime;
  let total: CurrencyAmount|null = null;
  while(time.lessThan(endTime)) {
    const t = time; // avoid warning about reference to var outside of loop
    const price = calendar.prices.find(p => {
      return p.day === dayOfWeek && t.isBetween(p.startTime, p.endTime, true, false);
    });
    if(price) {
      const priceEnd = price.endTime.lessThan(endTime) ? price.endTime : endTime;
      const multiplier = priceEnd.subtract(time).totalSeconds / settings.priceDuration.totalSeconds;
      const priceAmount = price.price.multiply(multiplier);
      total = total ? total.add(priceAmount) : priceAmount;
      time = priceEnd;
    } else {
      time = time.add(settings.increment);
    }
  }
  if(total && total.currency && includeTaxes) {
    total = applyTaxes(total, await loadTaxes(total.currency));
  }
  return total;
}

export function readTaxType(data: any) {
  const { rate, ...props } = data;
  return {
    rate: parseFloat(rate),
    ...props
  };
}

export async function loadTaxes(currency: Currency): Promise<TaxType[]> {
  let taxes = getCacheItem(TAXES_CACHE_KEY);
  if(taxes) {
    const currencyTaxes = taxes[currency.code];
    if(currencyTaxes) {
      return currencyTaxes;
    }
  }
  const response = await callAPI(`/taxes/?currency=${currency.code}`, 'GET');
  const currencyTaxes = response.map(readTaxType);
  if(!taxes) {
    taxes = {};
  }
  taxes[currency.code] = currencyTaxes;
  setCacheItem(TAXES_CACHE_KEY, taxes);
  return currencyTaxes;
}

function readSpaceWithSamplePrices(data: any): SpaceWithSamplePrices {
  const { sample_prices, currency, ...props } = data;
  const currencyObj: Currency = readCurrency(currency);
  return {
    samplePrices: sample_prices.map((data: any) => readSpaceSamplePrice(data, currencyObj)),
    ...props
  };
}

function readSpaceSamplePrice(data: any, currency: Currency): SpaceSamplePrice {
  const { start_day, end_day, start_time, end_time, price, ...props } = data;
  return {
    startDay: start_day,
    endDay: end_day,
    startTime: TimeSpan.parse(start_time),
    endTime: TimeSpan.parse(end_time),
    price: CurrencyAmount.parse(price, currency),
    ...props
  };
}

export async function loadSpaceSamplePrices(): Promise<SpaceWithSamplePrices[]> {
  let prices = getCacheItem(SAMPLE_PRICES_CACHE_KEY);
  if(prices) {
    return prices;
  }
  const response = await callAPI('/spaces/sample-prices/', 'GET');
  prices = response.map(readSpaceWithSamplePrices);
  setCacheItem(SAMPLE_PRICES_CACHE_KEY, prices);
  return prices;
}